// A20 — Reklama, DETAL + orphan-endpoint data-qatlami. Lane-C HOT fayllarга
// (`App.tsx`, `screenStatus.ts`, `i18n/*`, `lib/adapters.ts`, `permissions.ts`)
// TEGMAYDI — modul-lokal, o'zi-yetarli. Bu yerdagi fetcherlar backendда MAVJUD
// («orphan», UI-siz turgan) endpointlarni ULAYDI:
//   GET  /admin/ads/advertisers/:id            (A20.02 reklamachi kartasi)
//   POST /admin/ads/advertisers/:id/balance    (A20.03 balans to'ldirish)
//   GET  /admin/ads/campaigns/:id              (kampaniya kartasi)
//   GET/PUT /admin/ads/campaigns/:id/targeting (A20.07 targeting)
//   GET/POST /admin/ads/campaigns/:id/placements + DELETE /admin/ads/placements/:id (A20.08-09)
// Endpoint yo'q bo'lganlar (auction/brand-safety/fraud) fail-soft → bo'sh holat.
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'
import type { CampaignRow } from './adsApi'

const opt = (s?: AbortSignal) => (s ? { signal: s } : undefined)

/** Lokal qidiruv + klient sahifalash (adapters.ts private helperlarni takrorlaydi). */
function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

/** `{ <key>: T[] }` ro'yxatini GET qiladi; dormant endpoint (404/xato) → `[]`. */
async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/* ── A20.02 reklamachi kartasi ───────────────────────────────────── */
export interface AdvertiserDetail {
  id: number
  name: string
  status: string
  balance: number
  spent: number
  campaigns: number
  created_at: string
  campaign_list: CampaignRow[]
}
export const getAdvertiser = (id: number, s?: AbortSignal) =>
  api.get<{ advertiser: AdvertiserDetail }>(`/admin/ads/advertisers/${id}`, opt(s)).then((d) => d.advertiser)

/** A20.03 — POST /admin/ads/advertisers/:id/balance {amount} (musbat = to'ldirish). */
export const creditAdvertiser = (id: number, amount: number) =>
  api.post<{ ok: boolean }>(`/admin/ads/advertisers/${id}/balance`, { amount })

/* ── kampaniya kartasi (targeting/placements uchun sarlavha) ─────── */
export interface CreativeDetail {
  id: number
  kind: string
  status: string
  title: string | null
  body: string | null
  media_url: string | null
  reject_reason: string | null
  submitted_at: string
}
export interface CampaignDetail {
  id: number
  advertiser_id: number
  advertiser: string
  title: string
  status: string
  placement: string
  budget: number
  spent: number
  cpm: number
  impressions: number
  clicks: number
  link_url: string | null
  created_at: string
  starts_at: number | null
  ends_at: number | null
  creatives: CreativeDetail[]
}
export const getCampaign = (id: number, s?: AbortSignal) =>
  api.get<{ campaign: CampaignDetail }>(`/admin/ads/campaigns/${id}`, opt(s)).then((d) => d.campaign)

/* ── A20.07 targeting ────────────────────────────────────────────── */
export interface Targeting {
  campaign_id: number
  countries: string
  platforms: string
  min_age: number
  max_age: number
}
export const getTargeting = (id: number, s?: AbortSignal) =>
  api.get<{ targeting: Targeting }>(`/admin/ads/campaigns/${id}/targeting`, opt(s)).then((d) => d.targeting)
export const setTargeting = (
  id: number,
  body: { countries: string; platforms: string; min_age: number; max_age: number },
) => api.put<{ ok: boolean }>(`/admin/ads/campaigns/${id}/targeting`, body)

/* ── A20.08-09 placements (kanal allow/block ro'yxati) ───────────── */
export interface Placement {
  id: number
  channel_ref: string
  mode: string
  created_at: string
}
export const listPlacements = (id: number, s?: AbortSignal) =>
  api.get<{ placements: Placement[] }>(`/admin/ads/campaigns/${id}/placements`, opt(s)).then((d) => d.placements ?? [])
export const addPlacement = (id: number, channel_ref: string, mode: string) =>
  api.post<{ ok: boolean; id: number }>(`/admin/ads/campaigns/${id}/placements`, { channel_ref, mode })
export const deletePlacement = (placementId: number) =>
  api.del<{ ok: boolean }>(`/admin/ads/placements/${placementId}`)

/* ── A20.13 CPM — kampaniyalardan hosil qilinadi (real /campaigns) ─ */
export const fetchCampaignsList = (s?: AbortSignal) =>
  safeList<CampaignRow>('/admin/ads/campaigns', 'campaigns', s ?? new AbortController().signal)

/* ── A20.17 firibgar reklamalar (dormant endpoint → fail-soft []) ── */
export interface FraudRow {
  id: number
  campaign: string
  advertiser: string
  signal: string
  score: number
  status: string
  detected_at: string
}
export function fetchFraud(params: FetchParams): Promise<FetchResult<FraudRow>> {
  return safeList<FraudRow>('/admin/ads/fraud', 'entries', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.campaign} ${r.advertiser} ${r.signal}`),
  )
}

/* ── A20.14 auksion (dormant → default read-only) ────────────────── */
export interface AuctionSettings {
  model: string // 'second_price' | 'first_price'
  floor_cpm: number
  reserve_pct: number
  enabled: boolean
}
export const fetchAuction = async (s?: AbortSignal): Promise<AuctionSettings> => {
  try {
    const d = await api.get<{ auction: AuctionSettings }>('/admin/ads/auction', opt(s))
    return d?.auction ?? { model: 'second_price', floor_cpm: 0, reserve_pct: 0, enabled: false }
  } catch {
    return { model: 'second_price', floor_cpm: 0, reserve_pct: 0, enabled: false }
  }
}

/**
 * Modul-lokal RU stringlar (i18n HOT → REGISTER'да keyга ko'chiriladi).
 */
export const ADSD_STR = {
  // umumiy
  load: 'Загрузить',
  advertiserId: 'ID рекламодателя',
  campaignId: 'ID кампании',
  enterId: 'Введите ID и нажмите «Загрузить»',
  notFound: 'Не найдено',
  failed: 'Ошибка',
  saved: 'Сохранено',
  done: 'Готово',
  empty: 'Нет данных',
  save: 'Сохранить',
  add: 'Добавить',
  delete: 'Удалить',
  refresh: 'Обновить',
  status: 'Статус',
  created: 'Создан',
  // A20.02 advertiser card
  advertiserCard: 'Карточка рекламодателя',
  balance: 'Баланс',
  spent: 'Потрачено',
  campaigns: 'Кампании',
  campaignsCol: 'Кампании рекламодателя',
  title: 'Кампания',
  budget: 'Бюджет',
  cpm: 'CPM',
  impressions: 'Показы',
  clicks: 'Клики',
  // A20.03 balance
  balanceTitle: 'Баланс и пополнение',
  balanceDesc: 'Пополнение рекламного счёта рекламодателя',
  amount: 'Сумма',
  topup: 'Пополнить',
  topupOk: 'Баланс пополнен',
  amountHint: 'Положительная сумма зачисляется на баланс',
  available: 'Доступно',
  // A20.07 targeting
  targeting: 'Настройки таргетинга',
  targetingDesc: 'Страны, платформы и возраст аудитории кампании',
  countries: 'Страны (ISO, через запятую)',
  platforms: 'Платформы (android,ios,web)',
  minAge: 'Возраст от',
  maxAge: 'Возраст до',
  targetingHint: 'Пусто = без ограничения (показывается всем)',
  campaign: 'Кампания',
  advertiser: 'Рекламодатель',
  // A20.08-09 placements
  placements: 'Каналы (размещение)',
  placementsDesc: 'Список каналов allow/block для кампании',
  placementRemove: 'Исключить канал из рекламы',
  placementRemoveDesc: 'Удаление канала из списка размещения кампании',
  channelRef: 'Канал (@username / id)',
  mode: 'Режим',
  allow: 'Разрешить',
  block: 'Заблокировать',
  addChannel: 'Добавить канал',
  removeConfirm: 'Удалить это размещение?',
  // A20.12 placements overview
  placementsOverview: 'Рекламные места',
  placementsOverviewDesc: 'Инвентарь мест показа рекламы',
  feed: 'Лента (feed)',
  feedDesc: 'Нативная карточка в ленте сообщений/каналов',
  banner: 'Баннер (banner)',
  bannerDesc: 'Баннер в шапке или футере экрана',
  interstitial: 'Полноэкранная (interstitial)',
  interstitialDesc: 'Полноэкранное объявление между переходами',
  activeCampaigns: 'Активных кампаний',
  // A20.13 pricing
  pricing: 'Ценообразование (CPM)',
  pricingDesc: 'CPM по кампаниям: мин / средн / макс',
  minCpm: 'Мин. CPM',
  avgCpm: 'Средний CPM',
  maxCpm: 'Макс. CPM',
  perCampaign: 'CPM по кампаниям',
  // A20.14 auction
  auction: 'Настройки аукциона',
  auctionDesc: 'Модель торгов и минимальные ставки',
  auctionDormant: 'Аукцион ещё не сконфигурирован на бэкенде — значения по умолчанию (только чтение).',
  model: 'Модель',
  floorCpm: 'Минимальный CPM (floor)',
  reservePct: 'Резерв, %',
  enabled: 'Включён',
  yes: 'Да',
  no: 'Нет',
  // A20.17 fraud
  fraud: 'Мошеннические объявления',
  fraudSignal: 'Сигнал',
  fraudScore: 'Оценка',
  detectedAt: 'Обнаружено',
  fraudDormant: 'Детектор фрода активируется вместе с /admin/ads/fraud.',
  // A20.18 brand safety
  brandSafety: 'Brand safety',
  brandSafetyDesc: 'Запрещённые категории/ключевые слова (блок-лист брендовой безопасности)',
  note: 'Примечание',
  category: 'Категория',
  brandSafetyEmpty: 'Блок-лист пуст — категории добавляются на экране «Запрещённые категории».',
  // A20.19 reports
  reports: 'Рекламные отчёты',
  reportsDesc: 'Сводка по показам, кликам и доходу',
  advertisersCnt: 'Рекламодателей',
  totalSpend: 'Общий расход',
  ctr: 'CTR',
  channelRevenue: 'Доход каналов',
  channel: 'Канал',
  revenue: 'Доход',
  updated: 'Обновлён',
  topCampaigns: 'Топ кампаний',
} as const
